import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "hooks/useAuth";
import dashboardService from "services/dashboardService";
import { PageContainer, PageHeader } from "components/page";
import { formatDate } from "utils/format";

import WeatherWidget from "./components/WeatherWidget";
import KPICards from "./components/KPICards";
import PeopleGrowthChart from "./components/PeopleGrowthChart";
import ResponseTable from "./components/ResponseTable";
import ProfileCard from "./components/ProfileCard";
import ChurchHealthScore from "./components/ChurchHealthScore";
import ServiceCalendar from "./components/ServiceCalendar";
import LinkHubSummary from "./components/LinkHubSummary";

const RESPONSES_PER_PAGE = 8;

/**
 * Loads one dashboard card's data. Each card fails independently, so a slow or
 * broken endpoint never blanks the whole page. Responses that arrive after a
 * newer request started are dropped.
 */
const useCardData = (load) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const requestId = useRef(0);

  const run = useCallback(async () => {
    const id = ++requestId.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await load();
      if (id === requestId.current) setState({ data, loading: false, error: null });
    } catch (error) {
      if (id === requestId.current) setState({ data: null, loading: false, error });
    }
  }, [load]);

  useEffect(() => {
    run();
  }, [run]);

  return { ...state, reload: run };
};

const loadSummary = async () => (await dashboardService.getSummary())?.data?.data ?? null;
const loadGrowth = async () => (await dashboardService.getPeopleGrowth())?.data?.data ?? null;
const loadHealth = async () => (await dashboardService.getHealthScore())?.data?.data ?? null;
const loadCalendar = async () =>
  (await dashboardService.getUpcomingServices())?.data?.data ?? null;
const loadLinks = async () => (await dashboardService.getLinkAnalytics())?.data?.data ?? null;

const ChurchDashboard = () => {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("staff");
  const [currentPage, setCurrentPage] = useState(1);

  const summary = useCardData(loadSummary);
  const growth = useCardData(loadGrowth);
  const health = useCardData(loadHealth);
  const calendar = useCardData(loadCalendar);
  const links = useCardData(loadLinks);

  const loadResponses = useCallback(async () => {
    const res = await dashboardService.getResponses(activeTab, currentPage, RESPONSES_PER_PAGE);
    return { rows: res?.data?.data || [], pagination: res?.data?.pagination || null };
  }, [activeTab, currentPage]);
  const responses = useCardData(loadResponses);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1) setCurrentPage(page);
  };

  return (
    <PageContainer>
      <PageHeader
        title={`Welcome, ${user?.firstName || "there"}`}
        description={formatDate(new Date())}
        actions={<WeatherWidget />}
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-5 xl:col-span-2">
          <KPICards
            data={summary.data}
            loading={summary.loading}
            error={summary.error}
            onRetry={summary.reload}
          />
          <PeopleGrowthChart
            data={growth.data}
            loading={growth.loading}
            error={growth.error}
            onRetry={growth.reload}
          />
          <ResponseTable
            data={responses.data?.rows}
            pagination={responses.data?.pagination}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onPageChange={handlePageChange}
            loading={responses.loading}
            error={responses.error}
            onRetry={responses.reload}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <ProfileCard user={user} />
          <ChurchHealthScore
            data={health.data}
            loading={health.loading}
            error={health.error}
            onRetry={health.reload}
          />
          <ServiceCalendar
            data={calendar.data}
            loading={calendar.loading}
            error={calendar.error}
            onRetry={calendar.reload}
          />
          <LinkHubSummary
            data={links.data}
            loading={links.loading}
            error={links.error}
            onRetry={links.reload}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default ChurchDashboard;
